(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function setupReveal(root = document) {
    if (reducedMotion.matches) return;
    const targets = root.querySelectorAll(
      '#MainContent > .shopify-section:not(:first-child), .product-grid__item, .resource-list__item, .tcc-selected-products__grid > product-card'
    );

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('tcc-in-view'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, io) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('tcc-in-view');
          io.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -7% 0px', threshold: 0.06 }
    );

    targets.forEach((el, index) => {
      if (el.classList.contains('tcc-in-view')) return;
      el.classList.add('tcc-reveal');
      el.style.setProperty('--tcc-reveal-delay', `${Math.min(index % 6, 5) * 28}ms`);
      observer.observe(el);
    });
  }

  function setupSparkles() {
    if (reducedMotion.matches || document.body.dataset.tccSparkles === 'false') return;
    if (document.querySelector('.tcc-sparkle-canvas')) return;

    const canvas = document.createElement('canvas');
    canvas.className = 'tcc-sparkle-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.prepend(canvas);

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const density = Math.max(8, Math.min(42, Number(document.body.dataset.tccSparkleDensity) || 22));
    const brightness = Math.max(0.12, Math.min(0.75, Number(document.body.dataset.tccSparkleBrightness) || 0.38));
    let width = 0;
    let height = 0;
    let dpr = 1;
    let particles = [];
    let raf = 0;
    let lastFrame = 0;

    const makeParticle = () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      r: 0.35 + Math.random() * 1.15,
      phase: Math.random() * Math.PI * 2,
      speed: 0.00045 + Math.random() * 0.0011,
      strength: 0.22 + Math.random() * 0.78,
      cross: Math.random() > 0.82,
    });

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.round(density * Math.max(0.75, Math.min(1.45, (width * height) / 950000)));
      particles = Array.from({ length: count }, makeParticle);
    };

    const draw = (time) => {
      raf = requestAnimationFrame(draw);
      if (document.hidden || time - lastFrame < 33) return;
      lastFrame = time;
      ctx.clearRect(0, 0, width, height);

      for (const p of particles) {
        const pulse = Math.max(0, Math.sin(time * p.speed + p.phase));
        const alpha = pulse * pulse * p.strength * brightness;
        if (alpha < 0.025) continue;

        ctx.beginPath();
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.arc(p.x, p.y, p.r * (0.8 + pulse * 0.45), 0, Math.PI * 2);
        ctx.fill();

        if (p.cross && pulse > 0.82) {
          const arm = 2.5 + pulse * 3.5;
          ctx.strokeStyle = `rgba(235,242,248,${alpha * 0.5})`;
          ctx.lineWidth = 0.55;
          ctx.beginPath();
          ctx.moveTo(p.x - arm, p.y);
          ctx.lineTo(p.x + arm, p.y);
          ctx.moveTo(p.x, p.y - arm);
          ctx.lineTo(p.x, p.y + arm);
          ctx.stroke();
        }
      }
    };

    resize();
    window.addEventListener('resize', resize, { passive: true });
    raf = requestAnimationFrame(draw);

    reducedMotion.addEventListener('change', (event) => {
      if (!event.matches) return;
      cancelAnimationFrame(raf);
      canvas.remove();
    }, { once: true });
  }

  function init(root = document) {
    setupReveal(root);
    setupSparkles();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
